import Select, { OnChangeValue } from 'react-select';
import { useAsyncResource } from 'use-async-resource';
import { useTranslation } from 'react-i18next';
import { Filter, Option, User } from '../models/table-models';
import { ALL_MOBILE_ERROR_KINDS, MobileErrorKind } from '../models/mobile-error-models';
import { useGetUsers } from '../utils/users-hooks';

export interface FilterMultiSelectProps {
    filter: Filter;
    setFilter: React.Dispatch<React.SetStateAction<Filter>>;
}

const kindOptions: Option[] = ALL_MOBILE_ERROR_KINDS.map((k) => ({ value: k, name: k }));

/**
 * Filter for MobileErrors admin view. Two side-by-side selects:
 *
 *   - User picker → Filter.id (numeric userId as a string, matching the
 *     DistributorSelectFilter pattern used elsewhere). Mapped to
 *     {@code filterUserId} query param in mobile-error-models.toMobileErrorParams.
 *
 *   - Kind picker → Filter.tags (the backend honours one at a time; the UI is
 *     multi-select for forward-compat).
 *
 * Both filters apply server-side; combined with date range + search at the
 * wrapper level. Users are fetched once via useGetUsers limited to operator
 * roles (DISTRIBUTOR, INSTALLER) since those are the mobile-app users.
 */
export const MobileErrorsFilterSelect = ({ filter, setFilter }: FilterMultiSelectProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const [getUsers] = useAsyncResource(useGetUsers, undefined, 'DISTRIBUTOR,INSTALLER');
    const { items: users } = getUsers();

    const onKindChange = (value: OnChangeValue<Option, true>) => {
        const selected = value.map(({ value: v }) => v);
        setFilter({ ...filter, tags: selected });
    };

    const onUserChange = (user: OnChangeValue<User, false>) => {
        const id = user?.id ? String(user.id) : undefined;
        setFilter({ ...filter, id });
    };

    const selectedKinds = (filter.tags ?? [])
        .map((t) => kindOptions.find((o) => o.value === t))
        .filter((o): o is Option => Boolean(o));

    const selectedUser = users.find((u) => String(u.id) === filter.id) ?? null;

    return (
        <div className='w-100 d-flex justify-content-end gap-2 flex-wrap'>
            <Select<User, false>
                className='logs-select'
                placeholder={t('distributor')}
                isClearable
                value={selectedUser}
                options={users}
                getOptionLabel={(u) => u.name}
                getOptionValue={(u) => String(u.id)}
                onChange={onUserChange}
                backspaceRemovesValue
            />
            <Select
                className='logs-select'
                placeholder='Filter by kind'
                isMulti
                isClearable
                value={selectedKinds}
                options={kindOptions}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.value as MobileErrorKind}
                onChange={onKindChange}
                backspaceRemovesValue
            />
        </div>
    );
};
